/**
 * Shared helpers for system audit_logs field diffs and display (A => B).
 */

export type AuditFieldChange = {
  field: string;
  from: unknown;
  to: unknown;
};

const SENSITIVE_KEY_PATTERN = /(password|secret|token|authorization|cookie|credential)/i;

export function isSensitiveAuditKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERN.test(key);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/** Deep-clone plain JSON and strip sensitive keys. */
export function redactAuditPayload(value: unknown): Record<string, unknown> | null {
  if (!isPlainObject(value)) {
    return null;
  }
  const out: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value)) {
    if (isSensitiveAuditKey(key)) {
      continue;
    }
    if (Array.isArray(nested)) {
      out[key] = nested.map((item) => (isPlainObject(item) ? redactAuditPayload(item) : item));
    } else if (isPlainObject(nested)) {
      out[key] = redactAuditPayload(nested);
    } else {
      out[key] = nested;
    }
  }
  return out;
}

/**
 * Build field-level diffs for audit storage.
 * Prefers keys present in requestBody (what the client tried to change) on update.
 * On create (`before` is null), uses `after` keys so backend-owned fields
 * are included even when omitted from the request.
 */
export function buildAuditFieldChanges(input: {
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  requestBody: Record<string, unknown> | null;
}): AuditFieldChange[] {
  const { before, after, requestBody } = input;
  const requestKeys = requestBody
    ? Object.keys(requestBody).filter((key) => !isSensitiveAuditKey(key))
    : [];

  let keys: string[];
  if (before == null) {
    const afterKeys = Object.keys(after ?? {}).filter((key) => !isSensitiveAuditKey(key));
    keys = afterKeys.length > 0 ? afterKeys : requestKeys;
  } else if (requestKeys.length > 0) {
    keys = requestKeys;
  } else {
    const keySet = new Set<string>([
      ...Object.keys(before ?? {}),
      ...Object.keys(after ?? {}),
    ]);
    keys = Array.from(keySet).filter((key) => !isSensitiveAuditKey(key));
  }

  const changes: AuditFieldChange[] = [];
  for (const field of keys) {
    const from = before ? (before[field] ?? null) : null;
    const to = after
      ? (after[field] ?? null)
      : requestBody
        ? (requestBody[field] ?? null)
        : null;
    if (valuesEqual(from, to)) {
      continue;
    }
    changes.push({ field, from, to });
  }
  return changes;
}

export function formatAuditChangeValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    return value.length > 80 ? `${value.slice(0, 77)}...` : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  try {
    const serialized = JSON.stringify(value);
    if (serialized === undefined) {
      return '—';
    }
    return serialized.length > 80 ? `${serialized.slice(0, 77)}...` : serialized;
  } catch {
    return '—';
  }
}

/** Example: `status: inactive => active` */
export function formatAuditFieldChange(change: AuditFieldChange): string {
  return `${change.field}: ${formatAuditChangeValue(change.from)} => ${formatAuditChangeValue(change.to)}`;
}

/**
 * Display summary for the Audit Logs Changes column (first field only).
 * Supports `{ fieldChanges: [...] }` and legacy `{ old, new }`.
 */
export function formatAuditChangesSummary(changes: unknown): string {
  if (!isPlainObject(changes)) {
    return '—';
  }

  const fieldChanges = changes.fieldChanges;
  if (Array.isArray(fieldChanges) && fieldChanges.length > 0) {
    const first = fieldChanges[0];
    if (isPlainObject(first) && typeof first.field === 'string' && first.field.trim()) {
      return formatAuditFieldChange({
        field: first.field,
        from: first.from,
        to: first.to,
      });
    }
  }

  if (isPlainObject(changes.old) && isPlainObject(changes.new)) {
    const built = buildAuditFieldChanges({
      before: changes.old,
      after: changes.new,
      requestBody: null,
    });
    if (built[0]) {
      return formatAuditFieldChange(built[0]);
    }
  }

  return '—';
}
